/**
 * @file        kybManagerApi.js
 * @module      modules/kyb/manager/api
 * @description Calls to the KYB Manager backend (/api/kyb/manager) and the
 *              existing review decision endpoint. Reuses the app's axios
 *              instance (cookies, CSRF header and session refresh as
 *              everywhere else) and the KYB error shape.
 */

import { httpinstance } from "../../../../axios/api";
import { toKybError } from "../../api/kybApi";

const unwrap = (p) =>
  p.then(
    (r) => r.data?.data,
    (e) => {
      throw toKybError(e);
    },
  );

const base = "kyb/manager";

/** Drop empty filters so the URL stays clean. */
const params = (obj = {}) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ""));

export const kybManagerApi = {
  me: () => unwrap(httpinstance.get(`${base}/me`)),
  summary: () => unwrap(httpinstance.get(`${base}/summary`)),
  verifications: (q) => unwrap(httpinstance.get(`${base}/verifications`, { params: params(q) })),
  verification: (id) => unwrap(httpinstance.get(`${base}/verifications/${id}`)),
  apiCalls: (id) => unwrap(httpinstance.get(`${base}/verifications/${id}/api-calls`)),
  businesses: (q) => unwrap(httpinstance.get(`${base}/businesses`, { params: params(q) })),
  business: (id) => unwrap(httpinstance.get(`${base}/businesses/${id}`)),
  reviewItems: (q) => unwrap(httpinstance.get(`${base}/review-items`, { params: params(q) })),
  joinRequests: (q) => unwrap(httpinstance.get(`${base}/join-requests`, { params: params(q) })),
  /** The uploaded file as a Blob (sent with the session cookie). */
  file: (fileId, { download = false } = {}) =>
    httpinstance.get(`${base}/files/${fileId}`, { params: download ? { download: 1 } : {}, responseType: "blob" }).then(
      (r) => r.data,
      (e) => {
        throw toKybError(e);
      },
    ),
  /** decision: "approve" | "decline" */
  decide: (itemId, decision, notes) => unwrap(httpinstance.post(`kyb/review-items/${itemId}/decision`, { decision, notes: notes || undefined })),

  // Review console (cases)
  cases: (q) => unwrap(httpinstance.get(`${base}/cases`, { params: params(q) })),
  case: (id) => unwrap(httpinstance.get(`${base}/cases/${id}`)),
  team: () => unwrap(httpinstance.get(`${base}/team`)),
  assign: (ids, assigneeId) => unwrap(httpinstance.post(`${base}/cases/assign`, { ids, assigneeId: assigneeId || null })),
  addNote: (id, text) => unwrap(httpinstance.post(`${base}/cases/${id}/notes`, { text })),
  override: (id, body) => unwrap(httpinstance.post(`${base}/cases/${id}/override`, body)),
  approve: (id, body) => unwrap(httpinstance.post(`${base}/cases/${id}/approve`, body)),
  reject: (id, body) => unwrap(httpinstance.post(`${base}/cases/${id}/reject`, body)),
  requestDocuments: (id, body) => unwrap(httpinstance.post(`${base}/cases/${id}/request-documents`, body)),
  moveToReview: (id, body) => unwrap(httpinstance.post(`${base}/cases/${id}/move-to-review`, body)),
  reopen: (id, body) => unwrap(httpinstance.post(`${base}/cases/${id}/reopen`, body)),

  // A KYB manager's own team (KYB executives)
  myTeam: () => unwrap(httpinstance.get(`${base}/my-team`)),
  addTeamMember: (body) => unwrap(httpinstance.post(`${base}/my-team`, body)),
  setTeamMemberActive: (userId, active) => unwrap(httpinstance.post(`${base}/my-team/${userId}/status`, { active })),
  resendTeamMemberPassword: (userId) => unwrap(httpinstance.post(`${base}/my-team/${userId}/resend-password`, {})),

  // KYB manager accounts (ASAP admins)
  users: (q) => unwrap(httpinstance.get(`${base}/users`, { params: params(q) })),
  createUser: (body) => unwrap(httpinstance.post(`${base}/users`, body)),
  setUserActive: (userId, active) => unwrap(httpinstance.post(`${base}/users/${userId}/status`, { active })),
  resendPassword: (userId) => unwrap(httpinstance.post(`${base}/users/${userId}/resend-password`, {})),
};
